import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Fields keep a real border — `--input`, a step stronger than `--border` — because a
 * text box has to advertise where typing lands. It firms up further on hover and hands
 * over to the ring on focus, so the control is quiet at rest without ever being a
 * guessing game.
 */
const fieldBase = [
  'w-full min-w-0 rounded-md border border-input bg-card px-3 text-sm outline-none',
  'transition-[color,box-shadow,border-color] duration-150 hover:border-border-strong',
  'placeholder:text-muted-soft',
  'focus-visible:border-ring focus-visible:ring-ring/35 focus-visible:ring-[3px]',
  'disabled:cursor-not-allowed disabled:opacity-55',
  'aria-invalid:border-destructive aria-invalid:ring-destructive/25',
].join(' ');

/**
 * forwardRef is load-bearing, not cosmetic.
 *
 * `react-hook-form`'s `register()` returns a `ref` callback that must reach the real
 * DOM node. Spread onto a plain function component, React drops it silently — the field
 * then registers without an element, gets pruned as unmounted on submit, and disappears
 * from the form values. The form reports "Required" for a box the user visibly filled in.
 */
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(function Input(
  { className, type, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      data-slot="input"
      className={cn(
        fieldBase,
        'h-9 py-1',
        'file:text-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium',
        className,
      )}
      {...props}
    />
  );
});

const Textarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<'textarea'>>(
  function Textarea({ className, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        data-slot="textarea"
        className={cn(fieldBase, 'min-h-16 py-2 field-sizing-content resize-y', className)}
        {...props}
      />
    );
  },
);

export { Input, Textarea, fieldBase };
